import { Link, useParams } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { ScrollArea } from '@nucleus-commerce/ui/components/scroll-area';

import { GETTING_STARTED_SLUG, guides, type Guide } from '../content';
import { reference } from '../generated/reference';

import { MethodBadge } from './method-badge';

const itemClass =
  'text-muted-foreground hover:text-foreground hover:bg-accent data-[active=true]:bg-primary-tint data-[active=true]:text-primary-tint-foreground flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors data-[active=true]:font-medium';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-foreground px-2 text-xs font-semibold tracking-wide uppercase">
        {title}
      </h2>
      {children}
    </div>
  );
}

function GuideLink({ guide, onNavigate }: { guide: Guide; onNavigate?: () => void }) {
  const active = { 'data-active': 'true' };
  if (guide.slug === GETTING_STARTED_SLUG) {
    return (
      <Link
        to="/docs"
        activeOptions={{ exact: true }}
        activeProps={active}
        className={itemClass}
        onClick={onNavigate}
      >
        {guide.title}
      </Link>
    );
  }
  return (
    <Link
      to="/docs/guides/$slug"
      params={{ slug: guide.slug }}
      activeProps={active}
      className={itemClass}
      onClick={onNavigate}
    >
      {guide.title}
    </Link>
  );
}

export interface DocsSidebarProps {
  /** Closes the sheet below `lg` once a link is followed. */
  onNavigate?: () => void;
}

/**
 * Build with AI, the guides in frontmatter order and the Postman page, then the API reference by
 * tag with one link per operation.
 */
export function DocsSidebar({ onNavigate }: DocsSidebarProps) {
  const { t } = useTranslation('docs');
  const params = useParams({ strict: false });
  const activeTag = params.tag;
  // Explicit toggles win; otherwise only the tag of the open endpoint is expanded.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  return (
    <ScrollArea className="h-full">
      <nav aria-label={t('sidebar.label')} className="flex flex-col gap-6 px-3 py-6">
        <Section title={t('build.nav.group')}>
          <Link
            to="/docs/build"
            activeProps={{ 'data-active': 'true' }}
            className={itemClass}
            onClick={onNavigate}
          >
            {t('build.nav.builder')}
          </Link>
        </Section>
        <Section title={t('sidebar.guides')}>
          {guides.map((guide) => (
            <GuideLink key={guide.slug} guide={guide} onNavigate={onNavigate} />
          ))}
          <Link
            to="/docs/postman"
            activeProps={{ 'data-active': 'true' }}
            className={itemClass}
            onClick={onNavigate}
          >
            {t('postman.title')}
          </Link>
        </Section>
        <Section title={t('sidebar.reference')}>
          <Link
            to="/docs/api"
            activeOptions={{ exact: true }}
            activeProps={{ 'data-active': 'true' }}
            className={itemClass}
            onClick={onNavigate}
          >
            {t('sidebar.overview')}
          </Link>
          {reference.tags.map((tag) => {
            const open = toggled[tag.key] ?? tag.key === activeTag;
            return (
              <Collapsible
                key={tag.key}
                open={open}
                onOpenChange={(next) => setToggled((prev) => ({ ...prev, [tag.key]: next }))}
              >
                <CollapsibleTrigger className="text-foreground hover:bg-accent flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium">
                  <ChevronRight
                    aria-hidden
                    className="size-3.5 shrink-0 transition-transform [[data-state=open]>&]:rotate-90"
                  />
                  {tag.title}
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-0.5 py-0.5 pl-3">
                  {tag.operations.map((id) => {
                    const operation = reference.operations[id];
                    if (!operation) return null;
                    return (
                      <Link
                        key={id}
                        to="/docs/api/$tag/$operation"
                        params={{ tag: tag.key, operation: operation.slug }}
                        activeProps={{ 'data-active': 'true' }}
                        className={itemClass}
                        onClick={onNavigate}
                      >
                        <MethodBadge method={operation.method} size="xs" />
                        <span className="truncate">{operation.summary}</span>
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </Section>
      </nav>
    </ScrollArea>
  );
}
